// Pure helpers for the index analysis pages. Kept free of React so they can be
// unit-tested: invalid market data must never produce fabricated analysis
// (e.g. a "100% gap-down" when the feed reports open = 0 before the open).

/** A usable price: a finite number greater than zero. 0 is a feed placeholder. */
export function isValidPrice(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n) && n > 0;
}

export function fmt(n: number) {
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Formats a price, or an em dash when the value is missing/placeholder. */
export function fmtPrice(n: unknown) {
  return isValidPrice(n) ? fmt(n) : '—';
}

/** Position of price within the day's range, 0–100; null when inputs are invalid. */
export function rangePosition(price: number, low: number, high: number): number | null {
  if (![price, low, high].every(isValidPrice) || high < low) return null;
  if (high === low) return 50;
  return Math.min(100, Math.max(0, Math.round(((price - low) / (high - low)) * 100)));
}

/**
 * Plain-English, past-tense educational context for today's session.
 * Each line is emitted only when every input it relies on is valid; with no
 * valid data it returns [] and the page shows its "data unavailable" state.
 */
export function educationalContext(
  changePct: number,
  price: number,
  open: number,
  high: number,
  low: number,
  prevClose: number,
  indexName: string,
): string[] {
  const lines: string[] = [];

  if (isValidPrice(open) && isValidPrice(prevClose)) {
    const gapPct = ((open - prevClose) / prevClose) * 100;
    if (Math.abs(gapPct) > 0.3) {
      lines.push(`${indexName} opened with a ${Math.abs(gapPct).toFixed(2)}% ${gapPct > 0 ? 'gap-up' : 'gap-down'} from yesterday's close of ${fmt(prevClose)} — historically, large opening gaps often reflect overnight news or global market movements.`);
    } else {
      lines.push(`${indexName} opened near yesterday's close of ${fmt(prevClose)}, suggesting continuity from the previous session with no significant overnight gap.`);
    }
  }

  const pos = rangePosition(price, low, high);
  if (pos !== null) {
    const dayRangePct = ((high - low) / low) * 100;
    lines.push(`Today's trading range of ${fmt(low)} – ${fmt(high)} (${dayRangePct.toFixed(2)}% wide) places the current price at the ${pos}th percentile of today's range — ${pos > 65 ? 'closer to the day\'s high, historically associated with buying pressure' : pos < 35 ? 'closer to the day\'s low, historically associated with selling pressure' : 'mid-range, indicating indecision between buyers and sellers'}.`);
  }

  if (Number.isFinite(changePct) && isValidPrice(prevClose) && Math.abs(changePct) > 1.5) {
    lines.push(`A ${Math.abs(changePct).toFixed(2)}% move is historically considered a significant single-session move for ${indexName}. Such moves have historically been driven by major macro events, earnings surprises, or global risk-off/risk-on sentiment shifts.`);
  }

  return lines;
}
